import apiClient from './apiClient';
import toast from 'react-hot-toast';
import { getUserFriendlyError } from '../utils/errorHandlers';
import { generatePassword } from './userManagementService';

/**
 * Tenant (Clinic) Management Service
 * 
 * Handles CRUD operations for tenant/clinic management.
 * Ensures all operations persist to Supabase database.
 * 
 * @module tenantManagementService
 */

// ============================================================================
// VALIDATION HELPERS
// ============================================================================

/**
 * Generate subdomain from clinic name
 * @param {string} name - Clinic name
 * @returns {string} Subdomain (lowercase, hyphenated)
 */
export const generateSubdomain = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-'); // Remove duplicate hyphens
};

/**
 * Validate pagination parameters
 */
const validatePagination = (page, pageSize) => {
  if (typeof page !== 'number' || page < 0) {
    throw new Error('page must be a non-negative number');
  }
  if (typeof pageSize !== 'number' || pageSize < 1 || pageSize > 100) {
    throw new Error('pageSize must be between 1 and 100');
  }
};

// ============================================================================
// TENANT/CLINIC CRUD OPERATIONS
// ============================================================================

/**
 * List all tenants/clinics with pagination, filtering, and sorting
 * 
 * @param {Object} params - Query parameters
 * @param {string} [params.q] - Search query (searches name, city)
 * @param {string} [params.status] - Filter by status (active, inactive, suspended)
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=10] - Items per page
 * @param {string} [params.sortBy='created_at'] - Sort column
 * @param {string} [params.sortOrder='desc'] - Sort direction (asc, desc)
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const listTenants = async ({
  q,
  status,
  page = 0,
  pageSize = 10,
  sortBy = 'created_at',
  sortOrder = 'desc',
}) => {
  try {
    validatePagination(page, pageSize);

    // Build query params for API
    const params = {
      page,
      pageSize,
      sortBy,
      sortOrder,
    };

    if (status && status !== 'all') {
      params.status = status;
    }

    if (q && q.trim()) {
      params.q = q.trim();
    }

    // Call API endpoint
    const response = await apiClient.getTenants(params);

    return {
      data: {
        rows: response.rows || [],
        total: response.total || 0,
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
      },
      error: null,
    };
  } catch (error) {
    console.error('Error listing tenants:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Get a single tenant by ID
 * 
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const getTenant = async (tenantId) => {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    const data = await apiClient.getTenantById(tenantId);
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching tenant:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Create a new tenant/clinic with admin user
 * 
 * IMPORTANT: This creates both tenant AND clinic admin in database
 * 
 * @param {Object} tenantData - Tenant data
 * @param {string} tenantData.name - Clinic name
 * @param {string} [tenantData.location_city] - City
 * @param {string} [tenantData.phone] - Phone
 * @param {string} [tenantData.email] - Email
 * @param {Object} tenantData.adminData - Clinic admin data
 * @param {string} tenantData.adminData.first_name - Admin first name
 * @param {string} tenantData.adminData.last_name - Admin last name
 * @param {string} tenantData.adminData.email - Admin email
 * @param {string} [tenantData.adminData.phone] - Admin phone
 * @param {string} [tenantData.adminData.password] - Admin password (auto-generated if not provided)
 * @param {string} [tenantData.onboarded_by] - User ID of super admin creating this
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const createTenant = async (tenantData) => {
  try {
    const { name, location_city, phone, email, adminData } = tenantData;

    // Validate required fields
    if (!name) {
      throw new Error('Clinic name is required');
    }
    if (!adminData || !adminData.first_name || !adminData.last_name || !adminData.email) {
      throw new Error('Admin first name, last name, and email are required');
    }

    // Generate password for admin if not provided
    const adminPassword = adminData.password || generatePassword();

    if (import.meta.env.DEV) console.log('🏥 Creating clinic:', name);
    if (import.meta.env.DEV) console.log('📍 Location:', location_city || 'Not specified');
    if (import.meta.env.DEV) console.log('👤 Admin:', `${adminData.first_name} ${adminData.last_name}`);

    // Call API endpoint - backend handles tenant + admin creation
    const tenantPayload = {
      name,
      city: location_city,
      phone,
      email,
      adminData: {
        firstName: adminData.first_name,
        lastName: adminData.last_name,
        email: adminData.email,
        phone: adminData.phone,
        password: adminPassword,
      },
    };

    const response = await apiClient.createTenant(tenantPayload);

    toast.success(
      `Clinic "${name}" created successfully!\n\nAdmin Password: ${adminPassword}\n\n⚠️ Save this password!`,
      { duration: 10000 }
    );

    return {
      data: {
        tenant: response.tenant || response,
        admin: response.admin,
        adminPassword,
      },
      error: null,
    };
  } catch (error) {
    console.error('❌ Error creating tenant:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Update tenant information
 * 
 * @param {string} tenantId - Tenant ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const updateTenant = async (tenantId, updates) => {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    // Sanitize updates - don't allow changing id, subdomain, or timestamps
    const sanitizedUpdates = { ...updates };
    delete sanitizedUpdates.id;
    delete sanitizedUpdates.subdomain;
    delete sanitizedUpdates.created_at;
    delete sanitizedUpdates.onboarded_at;

    if (import.meta.env.DEV) console.log('📝 Updating tenant:', tenantId);

    // Call API endpoint
    const data = await apiClient.updateTenant(tenantId, sanitizedUpdates);

    if (import.meta.env.DEV) console.log('✅ Tenant updated');
    toast.success('Clinic updated successfully');
    return { data, error: null };
  } catch (error) {
    console.error('❌ Error updating tenant:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Update tenant status
 * 
 * @param {string} tenantId - Tenant ID
 * @param {string} status - New status (active, inactive, suspended)
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const updateTenantStatus = async (tenantId, status) => {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    const validStatuses = ['active', 'inactive', 'suspended'];
    if (!validStatuses.includes(status)) {
      throw new Error(`status must be one of: ${validStatuses.join(', ')}`);
    }

    if (import.meta.env.DEV) console.log('🔄 Updating tenant status:', tenantId, '→', status);

    // Use API endpoints for activate/deactivate
    let data;
    if (status === 'active') {
      data = await apiClient.activateTenant(tenantId);
    } else if (status === 'inactive') {
      data = await apiClient.deactivateTenant(tenantId);
    } else {
      // For other statuses, use update endpoint
      data = await apiClient.updateTenant(tenantId, { status });
    }

    if (import.meta.env.DEV) console.log('✅ Tenant status updated');
    toast.success(`Clinic status updated to ${status}`);
    return { data, error: null };
  } catch (error) {
    console.error('❌ Error updating tenant status:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { data: null, error };
  }
};

/**
 * Delete tenant (soft delete by setting status to 'inactive')
 * 
 * @param {string} tenantId - Tenant ID
 * @param {boolean} [hardDelete=false] - If true, permanently delete; otherwise soft delete
 * @returns {Promise<{success: boolean, error: null}|{success: false, error: Error}>}
 */
export const deleteTenant = async (tenantId, hardDelete = false) => {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    if (hardDelete) {
      if (import.meta.env.DEV) console.log('🗑️ Permanently deleting tenant:', tenantId);
      await apiClient.deleteTenant(tenantId);
      if (import.meta.env.DEV) console.log('✅ Tenant permanently deleted');
      toast.success('Clinic permanently deleted');
    } else {
      if (import.meta.env.DEV) console.log('🔒 Deactivating tenant:', tenantId);
      await apiClient.deactivateTenant(tenantId);
      if (import.meta.env.DEV) console.log('✅ Tenant deactivated');
      toast.success('Clinic deactivated successfully');
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('❌ Error deleting tenant:', error);
    const friendlyError = getUserFriendlyError(error);
    toast.error(friendlyError);
    return { success: false, error };
  }
};

// ============================================================================
// TENANT STATISTICS
// ============================================================================

/**
 * Get tenant statistics (user counts, etc.)
 * 
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<{data: Object, error: null}|{data: null, error: Error}>}
 */
export const getTenantStats = async (tenantId) => {
  try {
    if (!tenantId) {
      throw new Error('tenantId is required');
    }

    // Call API endpoint
    const stats = await apiClient.getTenantStats(tenantId);
    return { data: stats, error: null };
  } catch (error) {
    console.error('Error fetching tenant stats:', error);
    return { data: null, error };
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  updateTenantStatus,
  deleteTenant,
  getTenantStats,
  generateSubdomain,
};
